import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Check, Bookmark, BookmarkCheck, Share2, Printer, Download } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { SubjectIcon, subjectGradient } from "@/components/SubjectIcon";
import LOCard from "@/components/LOCard";
import { TIDS } from "@/constants/testIds";
import { toast } from "sonner";

export default function LODetail() {
    const { code } = useParams();
    const { t, subjectLabel, bookmarks, toggleBookmark, lang } = useApp();
    const [data, setData] = useState(null);
    const [copied, setCopied] = useState(false);
    const nav = useNavigate();

    useEffect(() => {
        api.getLO(code).then(setData).catch(() => {
            toast.error("LO code not found");
            nav("/search");
        });
    }, [code, nav]);

    if (!data) return <div className="max-w-5xl mx-auto px-4 py-12 text-center text-muted-foreground">{t.loading}</div>;

    const entry = data.entry;
    const related = data.related || [];
    const isBookmarked = bookmarks.includes(entry.code);
    const isOdia = /[\u0B00-\u0B7F]/.test(entry.description || "");

    const onCopy = async () => {
        await navigator.clipboard.writeText(`${entry.code}\n\n${entry.description}`);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };
    const onShare = async () => {
        const text = `${entry.code} (${t.classN(entry.class)} • ${subjectLabel(entry.subject)})\n${entry.description}`;
        if (navigator.share) { try { await navigator.share({ title: entry.code, text }); } catch {} }
        else { await navigator.clipboard.writeText(text); toast.success(t.copied); }
    };
    const onPrint = () => window.print();

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 md:pb-32">
            <Link to="/search" data-testid="back-to-search" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-5">
                <ArrowLeft className="h-4 w-4" />
                {t.search}
            </Link>

            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative overflow-hidden rounded-3xl bg-card border border-border/70 p-6 sm:p-8 shadow-sm mb-6"
            >
                <div className={`absolute -top-12 -right-12 h-48 w-48 rounded-full bg-gradient-to-br ${subjectGradient(entry.subject)} opacity-15 blur-3xl`} />
                <div className="relative">
                    <div className="flex items-start justify-between gap-3 mb-6">
                        <div className="flex items-center gap-3">
                            <div className={`h-14 w-14 rounded-3xl bg-gradient-to-br ${subjectGradient(entry.subject)} grid place-items-center text-white shadow-md`}>
                                <SubjectIcon subject={entry.subject} className="h-7 w-7" />
                            </div>
                            <div>
                                <div data-testid={TIDS.loDetailCode} className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">{entry.code}</div>
                                <div className={`text-sm text-muted-foreground font-medium ${lang === "od" ? "font-odia" : ""}`}>
                                    {t.classN(entry.class)} • {subjectLabel(entry.subject)}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">{t.description}</div>
                    <p data-testid={TIDS.loDetailDesc} className={`text-lg leading-relaxed text-foreground/90 ${isOdia ? "font-odia" : ""}`}>
                        {entry.description}
                    </p>

                    <div className="flex flex-wrap gap-2 mt-7 pt-5 border-t border-border/60">
                        <button onClick={onCopy} data-testid={`detail-copy-${entry.code.replace(/\s+/g, "-")}`} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold active:scale-95 transition-transform">
                            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                            {copied ? t.copied : t.copy}
                        </button>
                        <button
                            onClick={() => toggleBookmark(entry.code)}
                            data-testid={`detail-bookmark-${entry.code.replace(/\s+/g, "-")}`}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold active:scale-95 transition-transform ${
                                isBookmarked ? "bg-amber-500 text-white" : "bg-muted hover:bg-muted/70"
                            }`}
                        >
                            {isBookmarked ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
                            {isBookmarked ? t.bookmarked : t.bookmark}
                        </button>
                        <button onClick={onShare} className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted hover:bg-muted/70 text-sm font-semibold active:scale-95 transition-transform">
                            <Share2 className="h-4 w-4" /> {t.share}
                        </button>
                        <button onClick={onPrint} className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted hover:bg-muted/70 text-sm font-semibold active:scale-95 transition-transform">
                            <Printer className="h-4 w-4" /> {t.print}
                        </button>
                        <a
                            href={api.exportUrl("pdf", { class_num: entry.class, subject: entry.subject })}
                            className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted hover:bg-muted/70 text-sm font-semibold active:scale-95 transition-transform"
                        >
                            <Download className="h-4 w-4" /> PDF
                        </a>
                    </div>
                </div>
            </motion.div>

            {related.length > 0 && (
                <div data-testid={TIDS.loDetailRelated}>
                    <h2 className="font-display text-xl font-bold mb-3">{t.relatedCodes}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {related.map((e, i) => <LOCard key={e.id || e.code} entry={e} index={i} />)}
                    </div>
                </div>
            )}
        </div>
    );
}
